/* ============================================================================
 * ClientProposalView — client-facing proposal renderer for the Client Portal.
 *
 * Layout (intentional UX order):
 *   1. Project header (Proposal eyebrow + project name + client name)
 *   2. Project Overview (description, scope, address, dates, special conditions)
 *   3. Sections — each with its line items, materials, allowances, and section
 *      subtotal. Honors Bottom Line mode (line item costs hidden) per section.
 *   4. About allowances (only when allowances exist)
 *   5. Payment schedule (only when present)
 *   6. Grand total (subtotal / tax / total) — placed at the BOTTOM so the
 *      client reads the scope before seeing the price.
 *
 * This is deliberately a slimmer, portal-tuned layout — it does NOT render the
 * contractor brand header (which already appears at the top of the portal
 * page) and avoids contractor-only chrome like the "Draft" status badge.
 *
 * Reuses PreviewSection from EstimatePreview.js so the section rendering
 * (line item cost masking via Bottom Line mode, materials, allowances) stays
 * fully consistent with what the contractor sees in the print preview.
 * ========================================================================== */

import React from 'react';
import {
  ClipboardList,
  MapPin,
  Calendar as CalendarIcon,
  AlertTriangle,
  Wallet,
  Receipt,
  Layers,
  FileDiff,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PreviewSection } from '@/components/estimates/EstimatePreview';
import { ScopeProposalBody } from '@/components/estimates/ScopeProposal';
import { TMAgreementBody } from '@/components/estimates/TMAgreement';
import { computeEstimateTotals, formatUsd } from '@/lib/estimatesData';

const ALLOWANCE_HELP =
  'Allowances are placeholder budgets for items still being decided (e.g., tile, fixtures, lighting). They\u2019re listed here for transparency but are tracked separately from the contract subtotal.';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

/* ----------------------------------------------------------------------------
 * Project Overview block
 * ------------------------------------------------------------------------- */
const ProjectOverviewBlock = ({ overview }) => {
  const hasOverview = !!(
    (overview?.description && overview.description.trim()) ||
    (overview?.scope_summary && overview.scope_summary.trim()) ||
    (overview?.address && overview.address.trim()) ||
    (overview?.est_start && overview.est_start.trim()) ||
    (overview?.special_conditions && overview.special_conditions.trim())
  );
  if (!hasOverview) return null;

  return (
    <div
      className="rounded-md border border-border overflow-hidden"
      data-testid="client-proposal-overview"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-[hsl(var(--muted))]/40">
        <ClipboardList size={14} className="text-[hsl(var(--primary))]" />
        <h2 className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-foreground">
          Project overview
        </h2>
      </div>
      <div className="px-4 py-4 space-y-4">
        {overview?.description && overview.description.trim() ? (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
              Project description
            </div>
            <div
              className="text-sm text-foreground whitespace-pre-wrap leading-relaxed"
              data-testid="client-proposal-description"
            >
              {overview.description}
            </div>
          </div>
        ) : null}
        {overview?.scope_summary && overview.scope_summary.trim() ? (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
              Scope summary
            </div>
            <div
              className="text-sm text-foreground whitespace-pre-wrap leading-relaxed"
              data-testid="client-proposal-scope"
            >
              {overview.scope_summary}
            </div>
          </div>
        ) : null}
        {(overview?.address && overview.address.trim()) ||
        (overview?.est_start && overview.est_start.trim()) ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            {overview?.address && overview.address.trim() ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                  <MapPin size={11} />
                  Project address
                </div>
                <div
                  className="text-sm text-foreground"
                  data-testid="client-proposal-address"
                >
                  {overview.address}
                </div>
              </div>
            ) : null}
            {overview?.est_start && overview.est_start.trim() ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                  <CalendarIcon size={11} />
                  Estimated start
                </div>
                <div
                  className="text-sm text-foreground"
                  data-testid="client-proposal-est-start"
                >
                  {overview.est_start === 'TBD'
                    ? 'TBD'
                    : formatDate(overview.est_start)}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {overview?.special_conditions &&
        overview.special_conditions.trim() ? (
          <div className="pt-2 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1 inline-flex items-center gap-1.5">
              <AlertTriangle size={11} />
              Special conditions or notes
            </div>
            <div
              className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed"
              data-testid="client-proposal-conditions"
            >
              {overview.special_conditions}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * Payment schedule block
 * ------------------------------------------------------------------------- */
const PaymentScheduleBlock = ({ schedule }) => {
  if (!Array.isArray(schedule) || schedule.length === 0) return null;
  return (
    <div
      className="rounded-md border border-border overflow-hidden"
      data-testid="client-proposal-payment-schedule"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-[hsl(var(--muted))]/40">
        <Receipt size={14} className="text-[hsl(var(--primary))]" />
        <h2 className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-foreground">
          Payment schedule
        </h2>
      </div>
      <ul className="divide-y divide-border/60">
        {schedule.map((row) => (
          <li
            key={row.id || row.label}
            className="flex items-center justify-between px-4 py-2.5"
            data-testid={`client-proposal-schedule-${row.id || row.label}`}
          >
            <div className="min-w-0 pr-3">
              <div className="text-sm font-semibold text-foreground truncate">
                {row.label}{' '}
                <span className="text-xs text-muted-foreground">
                  ({row.percent}%)
                </span>
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Due {row.due || 'TBD'}
              </div>
            </div>
            <div className="text-sm font-semibold text-foreground tabular-nums shrink-0">
              {formatUsd(row.amount)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * Grand totals block — pinned at the bottom (after scope review)
 * ------------------------------------------------------------------------- */
const GrandTotalsBlock = ({ estimate, totals, changeOrders = [] }) => {
  const hasAllowances = (totals?.allowances_total || 0) > 0;
  const hasHOP = (totals?.hop_total || 0) > 0;
  // Approved change orders adjust the agreed price. They roll in on top of the
  // proposal total (they carry an agreed flat amount, same as co_total in the
  // app — not re-taxed), so we surface each one and a revised contract total.
  const approvedCos = (Array.isArray(changeOrders) ? changeOrders : []).filter(
    (co) => (co?.status || '') === 'approved'
  );
  const coSum = approvedCos.reduce((s, co) => s + (Number(co?.amount) || 0), 0);
  const revisedTotal = (Number(totals?.total) || 0) + coSum;
  return (
    <div
      className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/[0.04] overflow-hidden"
      data-testid="client-proposal-totals"
    >
      <div className="px-4 py-2.5 border-b border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))]/[0.06]">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold">
          Investment summary
        </div>
      </div>
      <div className="px-4 py-4 space-y-2">
        {hasHOP ? (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground inline-flex items-center gap-2">
              <Badge
                variant="outline"
                className="uppercase text-[9px] tracking-[0.16em] bg-blue-500/10 text-blue-300 border-blue-400/30 px-1.5 py-0"
              >
                H/O-P
              </Badge>
              Homeowner picks
            </span>
            <span
              className="text-foreground font-semibold tabular-nums"
              data-testid="client-proposal-hop-total"
            >
              {formatUsd(totals?.hop_total)}
            </span>
          </div>
        ) : null}
        {hasAllowances ? (
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-200/90 inline-flex items-center gap-2">
              <Wallet size={12} />
              Allowances (informational)
            </span>
            <span
              className="text-amber-200 font-semibold tabular-nums"
              data-testid="client-proposal-allowances-total"
            >
              {formatUsd(totals?.allowances_total)}
            </span>
          </div>
        ) : null}
        <div className="flex items-center justify-between text-sm pt-1 border-t border-border/60">
          <span className="text-muted-foreground">Subtotal</span>
          <span
            className="text-foreground font-semibold tabular-nums"
            data-testid="client-proposal-subtotal"
          >
            {formatUsd(totals?.subtotal)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Tax ({estimate?.tax_rate || 0}%)
          </span>
          <span
            className="text-foreground font-semibold tabular-nums"
            data-testid="client-proposal-tax"
          >
            {formatUsd(totals?.tax)}
          </span>
        </div>
        <div className="flex items-center justify-between border-t border-[hsl(var(--primary))]/25 pt-3 mt-2">
          <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold">
            {approvedCos.length > 0 ? 'Original total' : 'Total'}
          </span>
          <span
            className={cn(
              'tabular-nums',
              approvedCos.length > 0
                ? 'text-lg font-semibold text-foreground'
                : 'text-2xl font-bold text-[hsl(var(--primary))]'
            )}
            data-testid="client-proposal-total"
          >
            {formatUsd(totals?.total)}
          </span>
        </div>

        {approvedCos.length > 0 ? (
          <div
            className="mt-3 pt-3 border-t border-border/60 space-y-1.5"
            data-testid="client-proposal-change-orders"
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--success))] font-semibold mb-1">
              Approved changes
            </div>
            {approvedCos.map((co) => (
              <div
                key={co.id}
                className="flex items-center justify-between text-sm gap-3"
              >
                <span className="text-muted-foreground inline-flex items-center gap-1.5 min-w-0">
                  <FileDiff size={12} className="text-[hsl(var(--success))] shrink-0" />
                  <span className="truncate">{co.desc || 'Change order'}</span>
                </span>
                <span className="text-[hsl(var(--success))] font-semibold tabular-nums shrink-0">
                  +{formatUsd(co.amount)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-[hsl(var(--primary))]/25 pt-3 mt-2">
              <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold">
                Current total
              </span>
              <span
                className="text-2xl font-bold text-[hsl(var(--primary))] tabular-nums"
                data-testid="client-proposal-revised-total"
              >
                {formatUsd(revisedTotal)}
              </span>
            </div>
          </div>
        ) : null}

        {hasHOP ? (
          <div className="text-[11px] text-blue-300/80 pt-1 italic">
            Items flagged H/O-P are picked by the homeowner; the contractor
            still installs/coordinates them per scope.
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * Main client-portal proposal view
 *
 * `estimate` is a hydrated estimate row (with sections, overview, etc.).
 * `clientName` overrides the contact name shown in the header (the portal
 * already knows who the client is).
 * ------------------------------------------------------------------------- */
export const ClientProposalView = ({
  estimate,
  clientName = '',
  changeOrders = [],
}) => {
  if (!estimate) return null;
  const totals = computeEstimateTotals(estimate);
  const sections = Array.isArray(estimate?.sections) ? estimate.sections : [];
  const overview = estimate?.overview || {};
  const hasAllowances = (totals?.allowances_total || 0) > 0;
  const isScope = estimate?.layout === 'scope';
  // Time & Material agreements swap the priced-proposal body for the T&M
  // agreement (scope-of-work blocks + deposit/rate/markup/terms). Totals,
  // allowances, and payment schedule don't apply to a rate-based agreement.
  const isTM = estimate?.layout === 'tm';

  return (
    <div className="space-y-5" data-testid="client-proposal-view">
      {/* 1) Header — Proposal eyebrow + project + prepared for */}
      <div
        className="rounded-md border border-[hsl(var(--hairline))] bg-[hsl(var(--card))] px-4 sm:px-5 py-4"
        data-testid="client-proposal-header"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {isTM ? 'Time & Material Agreement' : 'Proposal'}
        </div>
        <h1 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
          {estimate?.project_name || estimate?.title || 'Project proposal'}
        </h1>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
          {clientName || estimate?.client_name ? (
            <div>
              <span className="uppercase tracking-[0.18em] text-[10px] mr-1.5">
                Prepared for
              </span>
              <span className="text-foreground font-medium">
                {clientName || estimate?.client_name}
              </span>
            </div>
          ) : null}
          {estimate?.created_at ? (
            <div className="sm:text-right">
              <span className="uppercase tracking-[0.18em] text-[10px] mr-1.5">
                Issued
              </span>
              <span className="text-foreground font-medium">
                {formatDate(String(estimate.created_at).slice(0, 10))}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Time & Material: agreement body instead of the priced proposal */}
      {isTM ? <TMAgreementBody estimate={estimate} /> : null}

      {!isTM ? (
      <>
      {/* 2) Project Overview */}
      <ProjectOverviewBlock overview={overview} />

      {/* 3) Sections — scope first, before price */}
      {sections.length > 0 ? (
        <div data-testid="client-proposal-sections">
          <div className="flex items-center gap-2 mb-2 px-1">
            <Layers size={13} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
              Scope of work
            </div>
          </div>
          {isScope ? (
            <ScopeProposalBody estimate={estimate} totals={totals} />
          ) : (
            <div className="space-y-5">
              {sections.map((section) => (
                <PreviewSection key={section.id} section={section} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div
          className="rounded-md border border-dashed border-border bg-[hsl(var(--muted))]/40 px-4 py-6 text-center text-sm text-muted-foreground"
          data-testid="client-proposal-no-sections"
        >
          Scope details haven&apos;t been added yet. Your contractor will share
          the full breakdown shortly.
        </div>
      )}

      {/* 4) About allowances (only when relevant) */}
      {hasAllowances ? (
        <div
          className="rounded-md border border-amber-400/30 bg-amber-500/5 p-4 text-[12px] text-amber-100/90 leading-relaxed"
          data-testid="client-proposal-allowance-explanation"
        >
          <div className="flex items-center gap-2 text-amber-200 font-semibold uppercase tracking-[0.18em] text-[11px] mb-1">
            <Wallet size={12} />
            About allowances
          </div>
          {ALLOWANCE_HELP}
        </div>
      ) : null}

      {/* 5) Payment schedule (when present) */}
      <PaymentScheduleBlock schedule={totals?.payment_schedule || []} />

      {/* 6) Grand total at the BOTTOM — read scope first, then price */}
      <GrandTotalsBlock
        estimate={estimate}
        totals={totals}
        changeOrders={changeOrders}
      />
      </>
      ) : null}
    </div>
  );
};

export default ClientProposalView;
